import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { PenSquare, Eye } from 'lucide-react';
import Header from './components/Layout/Header';
import PostForm from './components/Form/PostForm';
import PostPreview from './components/Preview/PostPreview';
import SavedPosts from './pages/SavedPosts';
import Settings from './pages/Settings';
import MobileTabView from './components/Shared/MobileTabView';
import DemoBanner from './components/Shared/DemoBanner';
import AIService from './services/aiService';

const HomePage = () => {
  // Load from sessionStorage on mount
  const [generatedPost, setGeneratedPost] = useState(() => {
    const saved = sessionStorage.getItem('currentPost');
    return saved ? JSON.parse(saved) : null;
  });
  const [formMetadata, setFormMetadata] = useState(() => {
    const saved = sessionStorage.getItem('currentPostMetadata');
    return saved ? JSON.parse(saved) : null;
  });
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('form');

  const handlePostGenerated = (postData, metadata) => {
    setGeneratedPost(postData);
    setFormMetadata(metadata);
    // Save to sessionStorage
    sessionStorage.setItem('currentPost', JSON.stringify(postData));
    sessionStorage.setItem('currentPostMetadata', JSON.stringify(metadata));
    // Switch to preview tab on mobile after generating
    setActiveTab('preview');
  };

  const handleRegenerate = async () => {
    if (!generatedPost) return;

    setIsRegenerating(true);
    try {
      const response = await AIService.regeneratePost(generatedPost.id);
      if (response.success) {
        setGeneratedPost(response.data);
      } else {
        alert('Failed to regenerate post: ' + response.error?.message);
      }
    } catch (error) {
      alert('Error regenerating post: ' + error.message);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleFeedback = async (feedback) => {
    if (!generatedPost) return;

    const response = await AIService.refinePost(generatedPost.content, feedback);
    if (response.success) {
      const updatedPost = {
        ...generatedPost,
        ...response.data
      };
      setGeneratedPost(updatedPost);
      // Update sessionStorage
      sessionStorage.setItem('currentPost', JSON.stringify(updatedPost));
    } else {
      throw new Error(response.error?.message || 'Failed to improve post');
    }
  };

  const handleSave = async (postData) => {
    if (!formMetadata) {
      alert('No post metadata available. Please generate a post first.');
      return;
    }

    try {
      const saveData = {
        title: formMetadata.title,
        content: postData.content,
        tone: formMetadata.tone,
        customTopics: formMetadata.customTopics,
        hashtags: postData.hashtags,
        lengthType: formMetadata.lengthType || 'medium',
        lengthMin: formMetadata.lengthMin || null,
        lengthMax: formMetadata.lengthMax || null
      };

      const response = await AIService.savePost(saveData);
      if (response.success) {
        alert('Draft saved successfully! You can view it in Saved Posts.');
      } else {
        alert('Failed to save draft: ' + response.error?.message);
      }
    } catch (error) {
      alert('Error saving draft: ' + error.message);
    }
  };

  const handleClearAll = () => {
    if (confirm('Clear the current post and form? This will reset everything.')) {
      sessionStorage.removeItem('currentPost');
      sessionStorage.removeItem('currentPostMetadata');
      sessionStorage.removeItem('postFormData');
      setGeneratedPost(null);
      setFormMetadata(null);
      setActiveTab('form');
      window.location.reload(); // Refresh to reset form
    }
  };

  const mobileTabs = [
    { id: 'form', label: 'إنشاء', icon: PenSquare },
    { id: 'preview', label: 'معاينة', icon: Eye, badge: generatedPost ? '1' : null }
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="text-center mb-6">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3">
          <span className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent">
            إنشاء منشور جديد
          </span>
        </h1>
        <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
          استخدم الذكاء الاصطناعي لإنشاء منشورات لينكد إن احترافية ومؤثرة تجذب جمهورك
        </p>

        {(generatedPost || formMetadata) && (
          <button
            onClick={handleClearAll}
            className="mt-4 px-5 py-2 text-sm font-medium text-red-600 hover:text-white hover:bg-red-500 bg-red-50 rounded-lg transition-all duration-300 hover:shadow-lg"
          >
            مسح الكل والبدء من جديد
          </button>
        )}
      </div>

      {/* Mobile Tab Navigation */}
      <MobileTabView
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabs={mobileTabs}
        className="mb-4 sticky top-0 z-10 bg-gradient-to-b from-gray-50 to-transparent pb-2"
      />

      {/* Two Column Layout - Desktop shows both, Mobile shows one based on tab */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Form Section */}
        <div className={`space-y-4 ${activeTab === 'form' ? 'block' : 'hidden lg:block'}`}>
          <div className="hidden lg:flex items-center gap-2 mb-4">
            <div className="w-1 h-6 bg-gradient-to-b from-blue-600 to-cyan-600 rounded-full"></div>
            <h2 className="text-xl font-semibold text-gray-900">تفاصيل المنشور</h2>
          </div>
          <PostForm onPostGenerated={handlePostGenerated} />
        </div>

        {/* Preview Section */}
        <div className={`space-y-4 ${activeTab === 'preview' ? 'block' : 'hidden lg:block'}`}>
          <div className="hidden lg:flex items-center gap-2 mb-4">
            <div className="w-1 h-6 bg-gradient-to-b from-cyan-600 to-blue-600 rounded-full"></div>
            <h2 className="text-xl font-semibold text-gray-900">معاينة</h2>
          </div>
          <PostPreview
            content={generatedPost?.content}
            hashtags={generatedPost?.hashtags || []}
            wordCount={generatedPost?.wordCount || 0}
            onSave={handleSave}
            onRegenerate={isRegenerating ? null : handleRegenerate}
            onFeedback={handleFeedback}
          />

          {/* Mobile: Back to Form Button */}
          {activeTab === 'preview' && (
            <div className="lg:hidden mt-4">
              <button
                onClick={() => setActiveTab('form')}
                className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <PenSquare size={18} />
                العودة للنموذج
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        {/* Demo Banner */}
        <DemoBanner />

        {/* Header */}
        <Header />

        {/* Main content */}
        <main className="flex-1">
          <div className="max-w-7xl mx-auto py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/history" element={<SavedPosts />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={
                <div className="flex items-center justify-center min-h-[60vh]">
                  <div className="text-center">
                    <div className="text-8xl mb-6">404</div>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-2">الصفحة غير موجودة</h1>
                    <p className="text-gray-600 text-lg">الصفحة التي تبحث عنها غير موجودة</p>
                  </div>
                </div>
              } />
            </Routes>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white/80 backdrop-blur-md border-t border-gray-200 mt-auto">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center gap-3">
              {/* Demo Badge */}
              <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-xs font-medium">
                نسخة تجريبية - Demo
              </span>
              {/* Copyright */}
              <p className="text-sm text-gray-600">
                &copy; {new Date().getFullYear()} م.مشاري دعجم
              </p>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
};

export default App;
